// A row or a table that scrolls sideways inside its own box says it goes on (QA-841): while there's
// more past an edge, the box carries data-more-start or data-more-end, and its stylesheet fades that
// edge out, so the last thing that shows reads as part of more, never as a word cut short. Nothing is
// marked while everything fits. `edgesOf` decides; `watchScrollEdges` keeps the marks on a box as it
// scrolls and resizes.

/** What a box shows of its content: how far it's scrolled, and how wide it and its content are. */
export interface ScrollBox {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

/** Whether there's more past the box's start and past its end (a pixel's slack each way). */
export function edgesOf(b: ScrollBox): { start: boolean; end: boolean } {
  const room = b.scrollWidth - b.clientWidth;
  if (room <= 1) return { start: false, end: false };
  return { start: b.scrollLeft > 1, end: b.scrollLeft < room - 1 };
}

function mark(el: HTMLElement): void {
  const e = edgesOf(el);
  el.toggleAttribute('data-more-start', e.start);
  el.toggleAttribute('data-more-end', e.end);
}

/**
 * Keeps `el`'s data-more-start and data-more-end true to what it shows, on scroll and whenever it or
 * one of its children changes size. Returns the way to stop.
 */
export function watchScrollEdges(el: HTMLElement | null): () => void {
  if (!el) return () => {};
  let frame = 0;
  const run = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => mark(el));
  };
  mark(el);
  el.addEventListener('scroll', run, { passive: true });
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(run) : null;
  ro?.observe(el);
  for (const c of el.children) ro?.observe(c);
  return () => {
    cancelAnimationFrame(frame);
    el.removeEventListener('scroll', run);
    ro?.disconnect();
  };
}
